import { useSelector } from "react-redux";
import Navigation from "../../common/components/Navigation/Navigation";
import { Outlet, useLocation } from "react-router-dom";
import {
  getCurrentElements,
  getSelectedProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { useTranslation } from "react-i18next";
import AddTeamModal from "../../features/team/components/AddTeamModal";
import ParticipantCsv from "../../features/team/components/ParticipantFromCsvModal";
import { useAppDispatch } from "src/common/state/hooks";
import { fetchFilteredMedias, resetMediaSearch } from "src/features/gallery/state/gallerySlice";
import { PROJECT_GROUP_MEDIAS_COMPONENT_ID } from "./ProjectGroupMedias";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { openPreviewInNewTab } from "src/common/util/openPreviewInNewTab";
import { setAlert } from "src/features/alert/state/alertsSlice";

const ProjectGroupEdit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");
  const lastUrlElement = path[path.length - 1];

  const jwt = useSelector(jwtSelector);
  const selectedProjectGroup = useSelector(getSelectedProjectGroup);
  const selectedCurrentElements = useSelector(getCurrentElements);

  // On page refresh, Redux state is empty (id=0). Router will redirect to home,
  // but render null in the meantime to avoid broken UI / bad API calls.
  if (!selectedProjectGroup.id) return null;

  const publishTabShouldBeVisible = selectedCurrentElements && selectedCurrentElements.length > 0;
  const isAnonymousCourse = selectedProjectGroup.auth_type === "none";

  const tabs = [
    { title: t("pages.projectGroup.tabs.list"), urlEndPoint: "list" },
    {
      title: t("general.preview"),
      urlEndPoint: "preview",
      preventNavigation: true, // Story 13.15: Render as button, not NavLink
      // Story 13.15: Clicking Preview tab opens external window without navigation
      onClick: async () => {
        if (selectedProjectGroup.id && jwt) {
          const result = await openPreviewInNewTab({
            projectGroupId: selectedProjectGroup.id,
            jwt,
            name: selectedProjectGroup.name,
          });
          if (!result.success) {
            dispatch(
              setAlert({
                msg: t("alerts.previewError"),
                type: "warning",
              }),
            );
          }
        }
      },
    },
    {
      title: t("general.medias"),
      urlEndPoint: "medias",
      onClick: () => {
        dispatch(resetMediaSearch({ resetEverything: true }));
        dispatch(
          fetchFilteredMedias({
            start: 0,
            limit: 20,
            projectGroupId: selectedProjectGroup.id,
            componentId: PROJECT_GROUP_MEDIAS_COMPONENT_ID,
          }),
        );
      },
    },
    { title: t("pages.projectGroup.tabs.team"), urlEndPoint: "team" },
    {
      title: t("pages.projectGroup.tabs.learners"),
      urlEndPoint: "learners",
      disabled: isAnonymousCourse,
      disabledTooltip: isAnonymousCourse ? t("pages.statistics.anonymousCourse") : undefined,
    },
    {
      title: t("pages.projectGroup.tabs.publish"),
      urlEndPoint: "publish",
      hidden: !publishTabShouldBeVisible,
    },
    { title: t("general.information"), urlEndPoint: "information" },
  ];

  return (
    <>
      <Navigation
        title={selectedProjectGroup.name}
        tabs={tabs}
        isProjectGroupView
        showGoBackButton
        backDepth={lastUrlElement === "new" ? 1 : 2}
      />
      {/* Story 13.15: Preview tab opens external window via onClick, no route navigation */}
      <Outlet />
      <AddTeamModal />
      <ParticipantCsv />
    </>
  );
};

export default ProjectGroupEdit;
